import { useEffect, useCallback } from "react";

import {
  connectTimeOffSocket,
  disconnectTimeOffSocket,
  getTimeOffSocket,
} from "src/Services/socket/timeOffSocket";

/**
 * Real-time updates for the Time Off admin approval queue.
 *
 * Subscribes to the global "time-off-notifications" room and triggers a refresh
 * callback whenever a request is created (from the frontend form or the
 * technician app) or decided, so the list stays current without a manual page
 * reload. Also refreshes when the user switches back to this browser tab.
 *
 * @param {Object} options
 * @param {Function} options.onRefresh - Reloads the time-off list.
 */
const useTimeOffRealtime = ({ onRefresh }) => {
  const stableRefresh = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  useEffect(() => {
    const socket = connectTimeOffSocket();

    const subscribe = () => {
      socket.emit("time-off-notifications:subscribe");
    };

    const handleChanged = (payload) => {
      if (!payload?.timeOffId) return;
      if (document.visibilityState === "visible") {
        stableRefresh();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      connectTimeOffSocket();
      subscribe();
      stableRefresh();
    };

    socket.on("connect", subscribe);
    socket.on("time-off-changed", handleChanged);

    if (socket.connected) {
      subscribe();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      const s = getTimeOffSocket();
      s.off("connect", subscribe);
      s.off("time-off-changed", handleChanged);
      s.emit("time-off-notifications:unsubscribe");
      disconnectTimeOffSocket();
    };
  }, [stableRefresh]);
};

export default useTimeOffRealtime;
